import { AddCandidateToListDialog } from "@components/candidates/AddCandidateToListDialog";
import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import {
  CREATE_CANDIDATE_POOL_VIEW,
  DELETE_CANDIDATE_POOL_VIEW,
  UPDATE_CANDIDATE_POOL_VIEW,
} from "@lib/graphql/mutations";
import { CANDIDATE_POOL_VIEWS_QUERY, CANDIDATES_QUERY } from "@lib/graphql/queries";
import { useBackgroundTaskCompletion } from "@lib/hooks/useBackgroundTaskCompletion";
import { useChatDataVersion } from "@lib/hooks/useChatDataVersion";
import { useDebounce } from "@lib/hooks/useDebounce";
import type { Connection } from "@lib/hooks/usePaginatedList";
import { usePaginatedList } from "@lib/hooks/usePaginatedList";
import {
  CANDIDATE_STAGES,
  type Candidate,
  type CandidatePoolSortBy,
  type CandidatePoolView,
  CONSENT_STATUSES,
} from "@lib/types";
import { ArrowDown, ArrowUp, Filter, Plus, Save, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useMutation, useQuery } from "urql";

type SortDirection = "ASC" | "DESC";

// Text columns default to ascending on first click; recency/reference columns
// to descending (newest / highest first) — matches the campaign list idiom.
const TEXT_SORTS: CandidatePoolSortBy[] = ["NAME", "CONSENT_STATUS"];

function nextDirection(
  current: { sortBy: CandidatePoolSortBy; sortDirection: SortDirection },
  column: CandidatePoolSortBy,
): SortDirection {
  if (current.sortBy === column) return current.sortDirection === "ASC" ? "DESC" : "ASC";
  return TEXT_SORTS.includes(column) ? "ASC" : "DESC";
}

function formatConsent(status: string | undefined): string {
  if (!status) return "—";
  return status.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

interface SortableHeaderProps {
  label: string;
  column: CandidatePoolSortBy;
  active: boolean;
  direction: SortDirection;
  onSort: (column: CandidatePoolSortBy) => void;
}

function SortableHeader({ label, column, active, direction, onSort }: SortableHeaderProps) {
  const Arrow = direction === "ASC" ? ArrowUp : ArrowDown;
  const ariaSort: React.AriaAttributes["aria-sort"] = active
    ? direction === "ASC"
      ? "ascending"
      : "descending"
    : "none";
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted"
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className="flex items-center gap-1 uppercase tracking-wide hover:text-text-primary"
      >
        {label}
        {active && <Arrow size={12} aria-hidden />}
      </button>
    </th>
  );
}

export function CandidateList() {
  const [search, setSearch] = useState("");
  // Debounce so keystrokes don't re-fire the query on every character
  // (structurally fixes the per-keystroke refetch, #2700).
  const debouncedSearch = useDebounce(search, 300);

  const [sortBy, setSortBy] = useState<CandidatePoolSortBy>("NAME");
  const [sortDirection, setSortDirection] = useState<SortDirection>("ASC");
  const [stageFilter, setStageFilter] = useState<string[]>([]);
  const [consentFilter, setConsentFilter] = useState<string[]>([]);
  const [activeSince, setActiveSince] = useState<string>(""); // "YYYY-MM-DD"
  const [showFilters, setShowFilters] = useState(false);

  const activeSinceIso = activeSince ? new Date(activeSince).toISOString() : undefined;
  const filtersActive = stageFilter.length > 0 || consentFilter.length > 0 || activeSince !== "";

  const variables = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      sortBy,
      sortDirection,
      stages: stageFilter.length ? stageFilter : undefined,
      consentStatuses: consentFilter.length ? consentFilter : undefined,
      activeSince: activeSinceIso,
    }),
    [debouncedSearch, sortBy, sortDirection, stageFilter, consentFilter, activeSinceIso],
  );

  const {
    nodes: candidates,
    fetching,
    hasNextPage,
    loadMore,
    refresh,
  } = usePaginatedList<{ candidates: Connection<Candidate> }, Candidate>({
    query: CANDIDATES_QUERY,
    variables,
    getConnection: (data) => data.candidates,
  });
  const navigate = useNavigate();
  useChatDataVersion(refresh);
  useBackgroundTaskCompletion("linkedin_import", refresh);

  const [{ data: viewsData }, refetchViews] = useQuery({ query: CANDIDATE_POOL_VIEWS_QUERY });
  const views: CandidatePoolView[] = viewsData?.candidatePoolViews ?? [];
  const [, createView] = useMutation(CREATE_CANDIDATE_POOL_VIEW);
  const [, updateView] = useMutation(UPDATE_CANDIDATE_POOL_VIEW);
  const [, deleteView] = useMutation(DELETE_CANDIDATE_POOL_VIEW);

  const [selectedViewId, setSelectedViewId] = useState("");
  const [savingView, setSavingView] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const [viewError, setViewError] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);

  function toggle(list: string[], value: string): string[] {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  }

  function handleSort(column: CandidatePoolSortBy) {
    setSortDirection(nextDirection({ sortBy, sortDirection }, column));
    setSortBy(column);
  }

  function currentViewInput() {
    return {
      sortBy,
      sortDirection,
      search: search || null,
      stages: stageFilter,
      consentStatuses: consentFilter,
      activeSince: activeSinceIso ?? null,
    };
  }

  function applyView(view: CandidatePoolView) {
    setSortBy(view.sortBy ?? "NAME");
    setSortDirection(view.sortDirection ?? "ASC");
    setStageFilter(view.stages ?? []);
    setConsentFilter(view.consentStatuses ?? []);
    setActiveSince(view.activeSince ? view.activeSince.slice(0, 10) : "");
    setSearch(view.search ?? "");
    if (view.stages?.length || view.consentStatuses?.length || view.activeSince)
      setShowFilters(true);
  }

  function handleSelectView(id: string) {
    setSelectedViewId(id);
    const view = views.find((v) => v.id === id);
    if (view) applyView(view);
  }

  async function handleSaveNewView() {
    if (!newViewName.trim()) return;
    setViewError(null);
    const result = await createView({ input: { name: newViewName.trim(), ...currentViewInput() } });
    const errors = result.data?.createCandidatePoolView?.errors ?? [];
    if (result.error) {
      setViewError(result.error.message);
      return;
    }
    if (errors.length) {
      setViewError(errors[0]);
      return;
    }
    setSavingView(false);
    setNewViewName("");
    const created = result.data?.createCandidatePoolView?.candidatePoolView;
    if (created) setSelectedViewId(created.id);
    refetchViews({ requestPolicy: "network-only" });
  }

  async function handleUpdateSelectedView() {
    if (!selectedViewId) return;
    setViewError(null);
    const result = await updateView({ input: { id: selectedViewId, ...currentViewInput() } });
    const errors = result.data?.updateCandidatePoolView?.errors ?? [];
    if (result.error || errors.length) {
      setViewError(result.error?.message ?? errors[0]);
      return;
    }
    refetchViews({ requestPolicy: "network-only" });
  }

  async function handleDeleteSelectedView() {
    if (!selectedViewId) return;
    await deleteView({ input: { id: selectedViewId } });
    setSelectedViewId("");
    refetchViews({ requestPolicy: "network-only" });
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-primary">Candidates</h1>
          <p className="mt-1 text-sm text-text-muted">Organisation-wide candidate pool</p>
        </div>
        <button
          type="button"
          onClick={() => setAdding((v) => !v)}
          className="flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
        >
          <Plus size={16} /> Add Candidate
        </button>
      </div>

      {/* Saved views + search + filter toggle */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <select
          aria-label="Saved views"
          value={selectedViewId}
          onChange={(e) => handleSelectView(e.target.value)}
          className="rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        >
          <option value="">All candidates</option>
          {views.map((view) => (
            <option key={view.id} value={view.id}>
              {view.name}
            </option>
          ))}
        </select>

        <div className="relative flex-1 min-w-[12rem]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-faint" />
          <input
            type="text"
            placeholder="Search candidates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md border border-input-border bg-card pl-10 pr-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>

        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          aria-pressed={showFilters}
          className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium ${
            filtersActive
              ? "border-accent text-accent"
              : "border-input-border bg-card text-text-secondary hover:bg-card-hover"
          }`}
        >
          <Filter size={16} /> Filters
        </button>

        {selectedViewId ? (
          <>
            <button
              type="button"
              onClick={handleUpdateSelectedView}
              className="flex items-center gap-1 rounded-md border border-input-border bg-card px-3 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover"
            >
              <Save size={14} /> Update view
            </button>
            <button
              type="button"
              onClick={handleDeleteSelectedView}
              aria-label="Delete view"
              className="rounded-md border border-input-border bg-card p-2 text-text-faint hover:bg-card-hover hover:text-red-600"
            >
              <Trash2 size={16} />
            </button>
          </>
        ) : savingView ? (
          <div className="flex items-center gap-1">
            <input
              type="text"
              // biome-ignore lint/a11y/noAutofocus: transient inline "name your view" field opened on demand
              autoFocus
              placeholder="View name"
              value={newViewName}
              onChange={(e) => setNewViewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveNewView();
                if (e.key === "Escape") setSavingView(false);
              }}
              className="rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
            <button
              type="button"
              onClick={handleSaveNewView}
              className="rounded-md bg-accent px-3 py-2 text-sm font-medium text-white hover:bg-accent-hover"
            >
              Save
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setSavingView(true);
              setViewError(null);
            }}
            className="flex items-center gap-1 rounded-md border border-input-border bg-card px-3 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover"
          >
            <Save size={14} /> Save view
          </button>
        )}
      </div>

      {viewError && (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {viewError}
        </p>
      )}

      {showFilters && (
        <div className="mt-3 rounded-lg border border-border bg-card-alt p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <fieldset>
              <legend className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                Stage
              </legend>
              <div className="mt-2 flex flex-wrap gap-1">
                {CANDIDATE_STAGES.map((stage) => (
                  <button
                    key={stage}
                    type="button"
                    aria-pressed={stageFilter.includes(stage)}
                    onClick={() => setStageFilter((f) => toggle(f, stage))}
                    className={`rounded-full border px-2.5 py-1 text-xs capitalize ${
                      stageFilter.includes(stage)
                        ? "border-accent bg-accent text-white"
                        : "border-input-border bg-card text-text-secondary hover:bg-card-hover"
                    }`}
                  >
                    {stage}
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset>
              <legend className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                Consent status
              </legend>
              <div className="mt-2 flex flex-wrap gap-1">
                {CONSENT_STATUSES.map((status) => (
                  <button
                    key={status}
                    type="button"
                    aria-pressed={consentFilter.includes(status)}
                    onClick={() => setConsentFilter((f) => toggle(f, status))}
                    className={`rounded-full border px-2.5 py-1 text-xs ${
                      consentFilter.includes(status)
                        ? "border-accent bg-accent text-white"
                        : "border-input-border bg-card text-text-secondary hover:bg-card-hover"
                    }`}
                  >
                    {formatConsent(status)}
                  </button>
                ))}
              </div>
            </fieldset>

            <div>
              <label
                htmlFor="candidate-active-since"
                className="text-xs font-semibold uppercase tracking-wide text-text-muted"
              >
                Active since
              </label>
              <input
                id="candidate-active-since"
                type="date"
                value={activeSince}
                onChange={(e) => setActiveSince(e.target.value)}
                className="mt-2 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
          </div>

          {filtersActive && (
            <button
              type="button"
              onClick={() => {
                setStageFilter([]);
                setConsentFilter([]);
                setActiveSince("");
              }}
              className="mt-3 text-xs font-medium text-accent hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {adding && (
        <div className="mt-4">
          <AddCandidateToListDialog
            target={{ kind: "org" }}
            heading="Add candidate"
            onDone={(candidateId) => {
              setAdding(false);
              if (candidateId) {
                navigate(`/candidates/${candidateId}`);
              } else {
                refresh();
              }
            }}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}

      {fetching && candidates.length === 0 ? (
        <p className="mt-4 text-sm text-text-muted">Loading...</p>
      ) : candidates.length === 0 ? (
        <div className="mt-4 rounded-lg border-2 border-dashed border-border-dashed p-8 text-center">
          <p className="text-sm text-text-muted">
            {debouncedSearch || filtersActive
              ? "No candidates match your search"
              : "No candidates yet"}
          </p>
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-lg bg-card shadow-sm ring-1 ring-ring">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-card-alt">
              <tr>
                <SortableHeader
                  label="Name"
                  column="NAME"
                  active={sortBy === "NAME"}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Current Role
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Company
                </th>
                <SortableHeader
                  label="Consent"
                  column="CONSENT_STATUS"
                  active={sortBy === "CONSENT_STATUS"}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Last activity"
                  column="LAST_ACTIVITY"
                  active={sortBy === "LAST_ACTIVITY"}
                  direction={sortDirection}
                  onSort={handleSort}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {candidates.map((candidate) => (
                <tr key={candidate.id} className="hover:bg-card-hover">
                  <td className="px-4 py-3 text-sm font-medium text-text-primary">
                    {typeof candidate.referenceNumber === "number" && (
                      <>
                        <CandidateReferenceBadge referenceNumber={candidate.referenceNumber} />{" "}
                      </>
                    )}
                    <Link
                      to={`/candidates/${candidate.id}`}
                      className="text-accent hover:underline"
                    >
                      {candidate.fullName}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {candidate.email || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {candidate.currentRoleTitle || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {candidate.currentCompany || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {formatConsent(candidate.consentStatus)}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary tabular-nums">
                    {formatDate(candidate.updatedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasNextPage && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={loadMore}
            disabled={fetching}
            className="rounded-md border border-input-border bg-card px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
          >
            {fetching ? "Loading..." : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
